import React from "react";
import { Mail, Download } from "lucide-react";
import { Bullet } from "./Bullet";
import { downloadFormationOnlineBrochure } from "../../utils/formationBrochure";

const MODULES = [
  { title: "module1_title", items: ["module1_1", "module1_2"] },
  { title: "module2_title", items: ["module2_1", "module2_2", "module2_3"] },
  { title: "module3_title", items: ["module3_1", "module3_2", "module3_3"] },
  { title: "module4_title", items: ["module4_1", "module4_2", "module4_3", "module4_4"] },
  { title: "module5_title", items: ["module5_1"] },
  { title: "module6_title", items: ["module6_1"] },
];

export default function FormationOnline({ t, lang }) {
  return (
    <div data-testid="formation-online-panel">
      <section className="container-page pb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-medium text-ink max-w-3xl leading-tight">
          {t("formationOnline.title")}
        </h1>
        <p className="mt-3 font-heading text-lg sm:text-xl text-ink-muted max-w-3xl">
          {t("formationOnline.subtitle")}
        </p>
        <button
          type="button"
          onClick={() => downloadFormationOnlineBrochure(t, lang)}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-medium text-cream-50 shadow-soft transition hover:bg-forest-dark"
          data-testid="formation-online-brochure-download"
        >
          <Download size={16} aria-hidden />
          {t("formationOnline.brochure_button")}
        </button>
      </section>

      {/* HEADING + INTRO */}
      <section className="container-page pb-6" data-testid="formation-online-overview">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink max-w-3xl leading-snug">
          {t("formationOnline.heading")}
        </h2>
        <p className="mt-5 text-ink-muted leading-relaxed max-w-3xl">
          {t("formationOnline.intro")}
        </p>
        <p className="mt-4 text-ink-muted leading-relaxed max-w-3xl">
          {t("formationOnline.trainers_intro")}
        </p>
        <p className="mt-4 text-ink-muted leading-relaxed max-w-3xl">
          {t("formationOnline.experience_text")}
        </p>
      </section>

      {/* FOR YOU */}
      <section className="container-page pb-10">
        <aside
          className="rounded-[1.25rem] border border-forest/15 bg-forest/5 p-6 sm:p-8"
          data-testid="formation-online-for-you"
        >
          <h3 className="font-heading text-xl sm:text-2xl text-ink mb-5">
            {t("formationOnline.for_you_title")}
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-ink-muted leading-relaxed">
            {["for_you_1", "for_you_2", "for_you_3", "for_you_4"].map((k) => (
              <Bullet key={k}>{t(`formationOnline.${k}`)}</Bullet>
            ))}
          </ul>
        </aside>
      </section>

      {/* MODULES */}
      <section className="container-page pb-10" data-testid="formation-online-modules">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-8">
          {t("formationOnline.modules_title")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {MODULES.map((mod) => (
            <div key={mod.title} className="card-soft p-6 sm:p-7">
              <p className="font-heading text-lg text-ink mb-3">{t(`formationOnline.${mod.title}`)}</p>
              <ul className="space-y-2 text-ink-muted leading-relaxed">
                {mod.items.map((k) => (
                  <Bullet key={k}>{t(`formationOnline.${k}`)}</Bullet>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* DATES & HORAIRES */}
      <section className="container-page pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          <div className="card-soft p-6 sm:p-8" data-testid="formation-online-dates">
            <h3 className="font-heading text-xl sm:text-2xl text-ink mb-4">
              {t("formationOnline.dates_title")}
            </h3>
            <ul className="space-y-3 text-ink-muted leading-relaxed">
              {["dates_1", "dates_2", "dates_3", "dates_4", "dates_5", "dates_6"].map((k) => (
                <Bullet key={k}>{t(`formationOnline.${k}`)}</Bullet>
              ))}
            </ul>
          </div>
          <div className="card-soft p-6 sm:p-8" data-testid="formation-online-hours">
            <h3 className="font-heading text-xl sm:text-2xl text-ink mb-4">
              {t("formationOnline.hours_title")}
            </h3>
            <p className="text-ink-muted leading-relaxed">{t("formationOnline.hours_text")}</p>
          </div>
        </div>
      </section>

      {/* FORMATEURS */}
      <section className="container-page pb-10" data-testid="formation-online-formateurs">
        <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-6">
          {t("formationOnline.formateurs_title")}
        </h2>
        <div className="card-soft p-6 sm:p-8">
          <ul className="space-y-4 text-ink-muted leading-relaxed">
            {["formateur_claire", "formateur_louise"].map((k) => (
              <Bullet key={k}>{t(`formationOnline.${k}`)}</Bullet>
            ))}
          </ul>
        </div>
      </section>

      {/* TARIFS */}
      <section className="container-page pb-16" data-testid="formation-online-tarifs">
        <div className="rounded-[1.25rem] border border-forest/15 bg-forest/5 p-6 sm:p-8">
          <h2 className="font-heading text-2xl sm:text-3xl text-ink mb-5">
            {t("formationOnline.tarifs_title")}
          </h2>
          <ul className="space-y-2 text-ink-muted leading-relaxed">
            {["tarifs_solidaire", "tarifs_personnel", "tarifs_commercial"].map((k) => (
              <Bullet key={k}>{t(`formationOnline.${k}`)}</Bullet>
            ))}
          </ul>
          <p className="mt-5 text-ink-muted leading-relaxed">
            {t("formationOnline.tarifs_signup")}{" "}
            <a
              href={`mailto:${t("formationOnline.tarifs_contact_email")}`}
              className="inline-flex items-center gap-1.5 text-forest hover:text-forest-dark underline underline-offset-4 break-all"
              data-testid="formation-online-contact-louise"
            >
              <Mail size={15} aria-hidden />
              {t("formationOnline.tarifs_contact_email")}
            </a>
          </p>
          <p className="mt-3 text-ink-muted leading-relaxed">{t("formationOnline.tarifs_deposit")}</p>
        </div>
      </section>
    </div>
  );
}
